import React, { useContext } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Card, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import ManufacturerItem from './ManufacturerItem'
import { useNavigate, useLocation } from 'react-router-dom'
import { ADD_VEHICLE_ROUTE } from '../../consts'
import { J1LeftarrowC } from '@e3dc-react/icons'
import { ManufacturerModel } from '../../models'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import ManufacturerLoading from './ManufacturerLoading'
import { ManufacturersContext } from '../../providers'
import { ManufacturerState } from '../../types'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useFetchChargingLocation } from '../../hooks'
import { useTypedSelector } from 'src/State/RootReducer'

const useStyles = makeStyles()(theme => ({
  header: {
    borderBottom: '1px solid',
    borderColor: theme.palette.shades.shade03,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    textAlign: 'center',
    margin: theme.spacing(1),
  },
  manufacturer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: 'auto',
    textAlign: 'center',
  },
}))

/*
 * It is a dialog with e-vehicle manufacturer list where we can choose a specific one to connect the vehicle.
 */
const VehicleManufacturersList: React.FC = () => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes } = useStyles()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))
  const navigate = useNavigate()
  const location = useLocation()
  const manufacturersContext = useContext(ManufacturersContext)
  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlagsContext()
  const installation = useTypedSelector(state => state.storage.installation)
  const { chargingLocation } = useFetchChargingLocation(installation?.id)
  const hasChargingLocation = Boolean(chargingLocation?.gpsLatitude && chargingLocation?.gpsLongitude)

  if (!manufacturersContext) return null
  const { hasError, loading, manufacturers } = manufacturersContext

  const handleCloseButtonClick = (): void => {
    const splittedPath = location.pathname.split(`/${ADD_VEHICLE_ROUTE}`)
    navigate(splittedPath[0])
  }

  const isLoading = loading || isFeatureFlagsLoading

  const disabledManufacturers = manufacturers
    .filter(
      manufacturer =>
        manufacturer.state === ManufacturerState.temporarilyUnavailable ||
        (!featureFlags.isShowNewVehicleVendorsEnabled && manufacturer.state === ManufacturerState.unchecked)
    )
    .toSorted((a, b) => a.name.localeCompare(b.name))

  return (
    <>
      {hasError && (
        <Box sx={{ width: '100%', pb: 2 }}>
          <Alert severity="error" text={t('emobility.vehicle-integration.manufacturer-fetching-failed')} />
        </Box>
      )}
      <Card>
        <Box className={classes.header} p={2}>
          <Box sx={{ flex: 1 }}>
            <IconButton color="primary" aria-label="close" onClick={handleCloseButtonClick}>
              <J1LeftarrowC
                sx={{
                  width: '30px',
                  height: '30px',
                }}
              />
            </IconButton>
          </Box>
          <Typography className={classes.title} variant="h3">
            {t('emobility.manufacturers-dialog.title')}
          </Typography>
          <Box sx={{ flex: 1 }} />
        </Box>
        <Box className={classes.manufacturer} p={2} width={isDesktop ? '75%' : '100%'} margin={'auto'}>
          <Typography variant="body2" p={2}>
            {t('emobility.manufacturers-dialog.description')}
          </Typography>
        </Box>
      </Card>
      <Typography sx={{ textAlign: 'center', mt: 2, mb: 2 }} variant="h4">
        {t('emobility.manufacturers-dialog.title-manufacturer')}
      </Typography>

      {isLoading && <ManufacturerLoading />}
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(240px, 480px))',
            md: 'repeat(3, minmax(240px, 480px))',
          },

          justifyContent: { md: 'center' },
          gap: 2,
        }}
      >
        {!isLoading &&
          manufacturers
            .filter(
              manufacturer =>
                manufacturer.state === ManufacturerState.available ||
                (featureFlags.isShowNewVehicleVendorsEnabled && manufacturer.state === ManufacturerState.unchecked)
            )
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((manufacturer: ManufacturerModel) => (
              <ManufacturerItem manufacturer={manufacturer} key={manufacturer.id} hasChargingLocation={hasChargingLocation} />
            ))}
      </Box>
      {disabledManufacturers.length > 0 && (
        <>
          <Typography sx={{ textAlign: 'center', mt: 2, mb: 2 }} variant="h4">
            {t('emobility.manufacturers-dialog.title-temporarily-disabled-manufacturer')}
          </Typography>
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(240px, 480px))',
                md: 'repeat(3, minmax(240px, 480px))',
              },

              justifyContent: { md: 'center' },
              gap: 2,
            }}
          >
            {!isLoading &&
              disabledManufacturers.map((manufacturer: ManufacturerModel) => (
                <ManufacturerItem manufacturer={manufacturer} key={manufacturer.id} disabled={true} hasChargingLocation={hasChargingLocation} />
              ))}
          </Box>
        </>
      )}
    </>
  )
}

export default VehicleManufacturersList
